import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in React Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Bright green icon for revealed locations
const revealedIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg width="32" height="32" viewBox="0 0 24 24" fill="#10B981" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="greenGlow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
          <feMerge> 
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      <circle cx="12" cy="12" r="9" fill="#A7F3D0" opacity="0.7"/>
      <path d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z" fill="#10B981" stroke="#FFFFFF" stroke-width="1.5" filter="url(#greenGlow)"/>
    </svg>`),
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16]
});

// Big glowing star for current active location
const activeIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg width="45" height="45" viewBox="0 0 24 24" fill="#F59E0B" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="glow">
          <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
          <feMerge> 
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        <animateTransform attributeName="transform" attributeType="XML"
          type="scale" values="1;1.2;1" dur="2s" repeatCount="indefinite"/>
      </defs>
      <circle cx="12" cy="12" r="10" fill="#FEF3C7" opacity="0.8"/>
      <path d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z" fill="#F59E0B" stroke="#FFFFFF" stroke-width="2" filter="url(#glow)"/>
    </svg>`),
  iconSize: [45, 45],
  iconAnchor: [22.5, 22.5],
  popupAnchor: [0, -22]
});

// Inactive location icon
const inactiveIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg width="24" height="24" viewBox="0 0 24 24" fill="#666666" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="8" stroke="#FFFFFF" stroke-width="1"/>
      <text x="12" y="16" text-anchor="middle" fill="#FFFFFF" font-size="10" font-weight="bold">?</text>
    </svg>`),
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12]
});

const InteractiveMap = ({ calendarDays, currentDay }) => {
  const centerPosition = [49.4553, 17.4514]; // Přerov coordinates

  // Helper function to get current Czech time
  const getCzechTime = () => {
    return new Date().toLocaleString("en-US", { timeZone: "Europe/Prague" });
  };

  // Helper function to check if day should be revealed (16:00 Czech time)
  const shouldDayBeRevealed = (day) => {
    const czechTimeString = getCzechTime();
    const czechTime = new Date(czechTimeString);
    
    const currentYear = czechTime.getFullYear();
    const revealDate = new Date();
    revealDate.setFullYear(currentYear, 11, day); // December (month 11), day
    revealDate.setHours(16, 0, 0, 0); // 16:00:00.000
    
    // Convert reveal date to Czech time for comparison
    const revealDateCzech = new Date(revealDate.toLocaleString("en-US", { timeZone: "Europe/Prague" }));
    
    return czechTime >= revealDateCzech;
  };

  // Helper function to check if day is currently active (today in Czech time)
  const isDayActive = (day) => {
    const czechTimeString = getCzechTime();
    const czechTime = new Date(czechTimeString);
    
    const currentYear = czechTime.getFullYear();
    const currentMonth = czechTime.getMonth();
    const currentDate = czechTime.getDate();
    
    // Check if today is the specific December day
    return currentMonth === 11 && currentDate === day && currentYear === 2025;
  };

  // Helper function to check if clue should be shown (from 00:00 Czech time)
  const shouldShowClue = (day) => {
    const czechTimeString = getCzechTime();
    const czechTime = new Date(czechTimeString);
    
    const currentYear = czechTime.getFullYear();
    const clueDate = new Date();
    clueDate.setFullYear(currentYear, 11, day); // December (month 11), day
    clueDate.setHours(0, 0, 0, 0); // 00:00:00.000
    
    // Convert clue date to Czech time for comparison
    const clueDateCzech = new Date(clueDate.toLocaleString("en-US", { timeZone: "Europe/Prague" }));
    
    return czechTime >= clueDateCzech;
  };

  return (
    <div className="relative">
      <MapContainer
        center={centerPosition}
        zoom={15}
        style={{ height: '500px', width: '100%' }}
        className="rounded-lg border-2 border-gold"
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        
        {calendarDays.map((day) => {
          const isRevealed = shouldDayBeRevealed(day.day);
          const isActive = isDayActive(day.day);
          
          return (
            <React.Fragment key={day.day}>
              {/* Show different circles based on status */}
              {!isRevealed && !isActive && (
                // Dark circles for future unrevealed days
                <Circle
                  center={day.coordinates}
                  radius={day.radiusKm * 1000}
                  pathOptions={{
                    color: '#2D3748',
                    fillColor: '#4A5568',
                    fillOpacity: 0.4,
                    weight: 3,
                    opacity: 0.8,
                    dashArray: '5, 5'
                  }}
                />
              )}
              
              {isActive && !isRevealed && (
                // Bright pulsing circle for today's active day
                <Circle
                  center={day.coordinates}
                  radius={day.radiusKm * 1000}
                  pathOptions={{
                    color: '#D4AF37',
                    fillColor: '#F7E98E',
                    fillOpacity: 0.3,
                    weight: 4,
                    opacity: 1,
                    dashArray: '2, 8'
                  }}
                  className="animate-pulse-gold"
                />
              )}
              
              {isRevealed && (
                // Bright revealed circle with marker
                <Circle
                  center={day.coordinates}
                  radius={day.radiusKm * 1000}
                  pathOptions={{
                    color: '#10B981',
                    fillColor: '#6EE7B7',
                    fillOpacity: 0.25,
                    weight: 3,
                    opacity: 0.9
                  }}
                />
              )}
              
              {/* Show markers for active day and revealed locations */}
              {(isRevealed || isActive) && (
                <Marker
                  position={day.coordinates}
                  icon={isRevealed ? revealedIcon : activeIcon}
                >
                  <Popup className="custom-popup">
                    <div className="text-center p-2">
                      <h3 className="font-bold text-gold text-lg mb-2">
                        Den {day.day}
                      </h3>
                      {isRevealed ? (
                        <div>
                          <p className="font-semibold mb-1">{day.location}</p>
                          <p className="text-sm text-gray-600 mb-2">{day.description}</p>
                          <div className="inline-flex items-center px-3 py-1 bg-green-400 text-gray-800 rounded-full text-sm font-semibold">
                            ⭐ Rozsvíceno!
                          </div>
                        </div>
                      ) : (
                        <div>
                          <p className="text-sm text-gray-500 mb-2">Rozsvítí se dnes v {day.revealTime}</p>
                          <div className="inline-flex items-center px-3 py-1 bg-gold text-dark-blue rounded-full text-sm font-semibold">
                            ✨ Dnes se rozsvítí!
                          </div>
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              )}
            </React.Fragment>
          );
        })}
      </MapContainer>
      
      {/* Legend */}
      <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-4 rounded-lg shadow-lg border border-gold/20">
        <h4 className="font-semibold text-dark-blue mb-2">Legenda</h4>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-4 h-4 bg-green-400 rounded-full border border-green-500"></div>
          <span className="text-sm">Rozsvíceno</span>
        </div>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-4 h-4 bg-gold rounded-full border border-yellow-400"></div>
          <span className="text-sm">Dnes se rozsvítí</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-gray-600 rounded-full border border-gray-500"></div>
          <span className="text-sm">Čeká na rozsvícení</span>
        </div>
      </div>
    </div>
  );
};

export default InteractiveMap;